import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import { showError, showSuccess, confirmDialog } from '../../utils/toast';
import './ServiceList.css';

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/services');
      // Sort by order_index
      const sorted = response.data.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      setServices(sorted);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
      showError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await confirmDialog(`Are you sure you want to delete "${name}"?`);
    if (!confirmed) return;

    try {
      await axios.delete(`/services/${id}`);
      showSuccess('Service deleted successfully!');
      setServices(services.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting service:', err);
      showError('Failed to delete service');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await axios.put(`/services/${id}`, {
        is_active: currentStatus ? 0 : 1
      });
      showSuccess(`Service ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchServices(); // Refresh list
    } catch (err) {
      console.error('Error updating service status:', err);
      showError('Failed to update service status');
    }
  };

  if (loading) {
    return (
      <div className="service-list-container">
        <div className="loading">Loading services...</div>
      </div>
    );
  }

  if (error && services.length === 0) {
    return (
      <div className="service-list-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="service-list-container">
      <div className="page-header">
        <h1>Services Management</h1>
        <Link to="/services/new" className="btn btn-primary">
          + Add New Service
        </Link>
      </div>

      {services.length === 0 ? (
        <div className="empty-state">
          <p>No services found. Add your first service!</p>
          <Link to="/services/new" className="btn btn-primary">
            + Add Service
          </Link>
        </div>
      ) : (
        <div className="table-container">
          <table className="services-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Description</th>
                <th>Route</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service.id}>
                  <td>{service.service_number || String(service.order_index || 0).padStart(2, '0')}</td>
                  <td>
                    <strong>{service.name}</strong>
                    <br />
                    <small className="text-muted">{service.slug}</small>
                  </td>
                  <td className="description-cell">
                    {service.description?.substring(0, 80)}
                    {service.description?.length > 80 ? '...' : ''}
                  </td>
                  <td>
                    <code>{service.route || '-'}</code>
                  </td>
                  <td>
                    <button
                      className={`status-badge ${service.is_active ? 'active' : 'inactive'}`}
                      onClick={() => toggleActive(service.id, service.is_active)}
                    >
                      {service.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link 
                        to={`/services/edit/${service.id}`}
                        className="btn btn-sm btn-edit"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(service.id, service.name)}
                        className="btn btn-sm btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ServiceList;
